'use client'

import React, { useEffect, useState, useMemo, useContext, useRef } from 'react'
import { PlayerInterface } from '@foundation-player/loader'
import VideoPlayer from '@/components/VideoPlayer/VideoPlayer'
import { type WidgetVideoProps, type VideoContext } from '@/types/cms'
import { type VideoPlayerProps } from '@/types/video'
import StructuredData from '@/elements/widgets/Video/StructuredData'
import { createPlayerProps, useSticky } from './utils'
import styles from './VideoDefault.module.scss'
import { MetaDataContext } from '@/context/MetadataContext'
import { PlayerEvent } from '@foundation-player/core'
import { Icon } from '@/components/Icon'
import ShareBtn from '@/components/ShareBtn/ShareBtn'
import AppContext from '@/types/context'

const WidgetVideoDefault = ({ item, context }: { item: WidgetVideoProps; context: AppContext }) => {
  const metaData = useContext(MetaDataContext)
  const [player, setPlayer] = useState<PlayerInterface | undefined>(undefined)
  const [playListIndex, setPlayListIndex] = useState<number>(0)
  const playerWrapper = useRef<HTMLDivElement>(null)
  const [stickyState, setStickyState] = useSticky(playerWrapper, player)
  const videoContext: VideoContext = item.items[playListIndex]

  const current = new Date(videoContext.model.publishedAt)
  const date = current.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  const dateTime = current.toLocaleString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  })

  const [playerProps, setPlayerProps] = useState<VideoPlayerProps | undefined>(
    videoContext
      ? createPlayerProps({
          context: videoContext,
          autoplay: true,
          heartbeatStarttype: playListIndex === 0 ? 0 : 1,
          cookies: context.cookies,
        })
      : undefined,
  )

  const videoPlayer = useMemo(
    () => (playerProps ? <VideoPlayer props={playerProps} providePlayer={setPlayer} /> : <></>),
    [playerProps],
  )

  useEffect(() => {
    const handleSessionEnd = () => {
      if (item.items[playListIndex + 1]) {
        setPlayerProps(
          createPlayerProps({
            context: item.items[playListIndex + 1],
            autoplay: true,
            heartbeatStarttype: 1,
            cookies: context.cookies,
          }),
        )
        setPlayListIndex(playListIndex + 1)
        setStickyState({ isSticky: false, trigger: 'sessionEnd' })
      }
    }

    if (player) {
      player.addEventListener(PlayerEvent.SessionEnd, handleSessionEnd)
    }

    return () => {
      if (player) {
        player.removeEventListener(PlayerEvent.SessionEnd, handleSessionEnd)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player])

  if (!(videoContext && playerProps)) return <></>

  return (
    <>
      <StructuredData item={item.items[0]} />
      {metaData?.model.isPreview && (
        <a
          href={item.items[0].model.video['web-prog']}
          target="_blank"
          style={{ display: 'block', marginBottom: '20px' }}
        >
          &gt;&gt; video only
        </a>
      )}
      <div className={styles.container}>
        <div className={styles.intro}>
          <h2 className={styles.kicker}>{videoContext.model.kicker}</h2>
          <h3 className={styles.title}>{videoContext.model.title}</h3>
        </div>

        <div className={styles.video}>
          <div className={styles.player}>
            <div
              ref={playerWrapper}
              className={stickyState.isSticky ? styles.sticky + ' ' + styles['sticky-article'] : undefined}
            >
              <div>
                {videoPlayer}
                {stickyState.isSticky && (
                  <button
                    className={styles.close}
                    onClick={() => {
                      player?.pause()
                      setStickyState({ isSticky: false, trigger: 'close' })
                    }}
                  >
                    <Icon name="close" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles['caption-container']}>
          <div className={styles['share-btn']}>
            <ShareBtn url={globalThis?.window?.location?.href} withText />
          </div>
          <span className={styles.date}>
            {date} | {dateTime} Uhr
          </span>
          <span className={styles['lead-text']}>{videoContext.model.leadtext}</span>
        </div>
      </div>
    </>
  )
}

export default WidgetVideoDefault
